import {Content, IonicPage, ModalController, NavController, NavParams} from 'ionic-angular';
import {Component, HostListener, Inject, OnDestroy, ViewChild} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {AppConfig} from '../../../../app/app.config';
import {
  JackingFormInstanceEditor,
  JackingFormConflictResult,
  JackingSectionGroupTemplateEditor,
  JackingSectionTemplateEditor
} from '../../../../models/jacking-v2/jacking.models';
import {JackingV2Service} from '../../../../services/jacking-v2/jacking-v2.service';
import {PtLuxonDate} from '../../../../models/projects/date.models';
import {faSave} from '@fortawesome/free-solid-svg-icons';
import {VesselService} from '../../../../services/vessel/vessel.service';
import {DirtyTracker, DirtyTrackingService} from '../../../../services/jacking-v2/dirty-tracking.service';
import {buildJackingDirtySnapshot} from '../../../../services/jacking-v2/jacking-form-dirty-snapshot';
import {JackingActivityFileUploadModalComponent} from '../../v1/jacking-activity-file-upload-modal/jacking-activity-file-upload-modal';
import {JackingCancelModalComponent} from '../components/jacking-cancel-modal/jacking-cancel-modal.component';
import {JackingCompleteModalComponent} from '../components/jacking-complete-modal/jacking-complete-modal.component';
import {ValidationResultModel} from '../../../../_foundation/models/validation.models';
import {getJackingSectionStatusDetail, JackingSectionStatus} from '../helpers/jacking-section-status.helper';
import {DateService} from '../../../../services/date/date.service';
import {CompleteDayService} from '../../../../services/complete-day/complete-day.service';
import {CompleteDayFilters} from '../../../../models/complete-day.models';
import {SupportContextKey} from '../../../../models/support/support.models';
import {SupportContextService} from '../../../../services/support/support-context.service';
import {
  JackingV2ConcurrentEditCoordinator,
  JackingV2ConcurrentEditHost
} from '../../../../services/jacking-v2/jacking-v2-concurrent-edit-coordinator.service';

@IonicPage()
@Component({
  selector: 'page-jacking-activity-edit-v2',
  templateUrl: 'jacking-activity-edit-v2.html',
  providers: [JackingV2ConcurrentEditCoordinator]
})

export class JackingActivityV2EditPage implements OnDestroy, JackingV2ConcurrentEditHost {
  @ViewChild('mainContent') mainContent: Content;

  config = AppConfig.settings;

  dailyLogDate: PtLuxonDate;
  editor: JackingFormInstanceEditor;
  loading = false;
  activeSectionIndex = 0;
  legsCount?: number = 4;

  disableDirtyRecord = false;
  showNavigationWarning = false;
  private dirty: DirtyTracker<JackingFormInstanceEditor>;
  isReadOnly: boolean = false;
  validationResult: ValidationResultModel | null = null;
  protected readonly faSave = faSave;
  private sectionStatusDebugByCode = new Map<string, string>();
  private focusFirstFieldOnNextNavigation = false;
  completedDayByIso: { [isoDate: string]: boolean } = {};

  constructor(public navParams: NavParams,
              private jackingV2Service: JackingV2Service,
              private vesselService: VesselService,
              private dirtyTracking: DirtyTrackingService,
              private modalController: ModalController,
              private navCtrl: NavController,
              private dateService: DateService,
              private completeDayService: CompleteDayService,
              private supportContextService: SupportContextService,
              public concurrentChanges: JackingV2ConcurrentEditCoordinator,
              @Inject(DOCUMENT) private document: Document
              ) {
    this.dirty = this.createDirtyTracker();
  }

  async ionViewWillEnter() {
    const jackingInstanceId = this.navParams.get('jackingInstanceId');
    this.dailyLogDate = this.navParams.get('currentDate');

    this.loading = true;
    try {
      const [editor, vesselEditor] = await Promise.all([
        this.jackingV2Service.getFormInstance(this.dailyLogDate.dateTime.toMillis(), jackingInstanceId),
        this.vesselService.getEditor(),
      ]);

      this.editor = editor;
      this.concurrentChanges.initialize(this, editor, vesselEditor?.id);
      this.legsCount = vesselEditor?.legsCount || 4;
      await this.refreshCompletedDayState();
      this.applyLockedPreviousDayStateToEvents();
      this.setSupportContext();
      this.concurrentChanges.start();
    } finally {
      this.showNavigationWarning = false;
      this.dirty.markClean(this.editor);
      this.isReadOnly = (this.editor.statusId as string).toLowerCase() === 'cancelled';
      this.loading = false;
    }
  }

  ionViewWillLeave(): void {
    this.concurrentChanges.stop();
  }

  ionViewDidEnter(): void {
    if (this.editor) {
      this.concurrentChanges.start();
    }
  }

  ngOnDestroy(): void {
    this.concurrentChanges.destroy();
  }

  public ionViewCanLeave(): boolean {
    if (this.disableDirtyRecord || this.showNavigationWarning) {
      return true;
    }

    if (this.dirty.isDirty(this.editor)) {
      this.showNavigationWarning = true;
      if (this.mainContent) {
        this.mainContent.scrollTop = 0;
      }
      return false;
    }

    return true;
  }

  goToSection(i: number, focusFirstField = false) {
    this.activeSectionIndex = i;
    this.setSupportContext();

    if (focusFirstField) {
      this.focusFirstFieldInActiveSection();
    }
  }

  goToLegsFreeSection() {
    this.goToSection(0, true);
    if (this.mainContent) {
      this.mainContent.scrollTop = 0;
    }
  }

  onNextButtonKeyDown(event: KeyboardEvent) {
    // Only keyboard activation with Enter should move focus into the next section.
    // Mouse clicks still navigate, but they leave focus behavior unchanged.
    this.focusFirstFieldOnNextNavigation = event.key === 'Enter';
  }

  goToNextSection() {
    const focusFirstField = this.focusFirstFieldOnNextNavigation;
    this.focusFirstFieldOnNextNavigation = false;
    this.goToSection(this.activeSectionIndex + 1, focusFirstField);
  }

  getActiveSection() {
    const sections = this.getSectionToShow();
    return sections[this.activeSectionIndex];
  }

  // remove 'section x -' from the title if it is present
  getShortSectionTitle(fullTitle: string) {
    if (!fullTitle) {
      return '';
    }
    return fullTitle.replace(/^Section\s*\d+\s*[-–]\s*/i, '').split(' and ')[0];
  }

  // add 'section x -' to the title if not already present
  getLongSectionTitle(section: JackingSectionTemplateEditor) {
    if (!section.title) {
      return '';
    }
    if (section.title.toLowerCase().startsWith('section')) {
      return section.title;
    }
    // e.g. "Project Data" -> "Section 1 - Project and Location Data"
    return 'Section ' + section.pageOrder + ' - ' + section.title;
  }

  // convenience for template
  getSectionToShow() {
    if (!this.editor) {
      return [];
    }
    return this.editor.template.sections
      .filter(s => s.enabled)
      .sort((a, b) => a.pageOrder - b.pageOrder);
  }

  getGroups(section: JackingSectionTemplateEditor) {
    return (section.groups || []).sort((a, b) => a.displayOrder - b.displayOrder);
  }

  shouldUseWideEventsLayout(section: JackingSectionTemplateEditor): boolean {
    if (!section || this.getRenderedLegCount() !== 6) {
      return false;
    }

    return this.getGroups(section).some(group => group.componentKey === 'JackingEventsTable');
  }


  getFields(group: JackingSectionGroupTemplateEditor) {
    return (group.fields || []).sort((a, b) => a.order - b.order);
  }

  async save() {
    this.loading = true;
    try {
      this.editor = await this.jackingV2Service.saveFormInstance(this.dailyLogDate.dateTime.toMillis(), this.editor);
      await this.refreshCompletedDayState();
      this.applyLockedPreviousDayStateToEvents();
      this.dirty.markClean(this.editor);
      this.navCtrl.pop();
    } catch (e) {
      if (e?.status === 409) {
        await this.concurrentChanges.handleConflict(e?.error as JackingFormConflictResult);
        return;
      }
      const validation = (e as any)?.validationResult || e?.error || null;
      if (e?.status === 400 && validation?.errors?.length) {
        this.validationResult = validation;
        if (this.mainContent) { this.mainContent.scrollTop = 0; }
        return;
      }
    } finally {
      this.loading = false;
    }
  }

  get activitySpansMultipleDays(): boolean {
    if (!this.editor?.startDate?.dateTime) {
      return false;
    }

    const timeBounds = this.getFormTimeBounds();
    if (!timeBounds) {
      return false;
    }

    return !this.dateService.compareDayOffsetsPreApplied(timeBounds.minDateTime, timeBounds.maxDateTime);
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    this.dirtyTracking.handleBeforeUnload(
      event,
      this.dirty.isDirty(this.editor),
      this.disableDirtyRecord
    );
  }

  // Even with the footer utility buttons removed from tab order, some sections
  // still use explicit positive tabIndex values (for example the final position grid).
  // Native browser tabbing can therefore skip the primary action, so we force the
  // last tabbable control in the active section to hand off to Next/Complete.
  @HostListener('document:keydown', ['$event'])
  onDocumentKeyDown(event: KeyboardEvent) {
    if (event.key !== 'Tab' || event.shiftKey || this.loading || !this.editor) {
      return;
    }

    const activeSectionCard = this.document.querySelector('.section-card') as HTMLElement | null;
    const primaryActionButton = this.document.querySelector('.footer-group.right .next-btn:not([disabled])') as HTMLElement | null;
    const activeElement = this.document.activeElement as HTMLElement | null;

    if (!activeSectionCard || !primaryActionButton || !activeElement || !activeSectionCard.contains(activeElement)) {
      return;
    }

    const tabbableElements = this.getTabbableElements(activeSectionCard);
    if (!tabbableElements.length || tabbableElements[tabbableElements.length - 1] !== activeElement) {
      return;
    }

    event.preventDefault();
    primaryActionButton.focus();
  }

  private createDirtyTracker() {
    return this.dirtyTracking.createTracker<JackingFormInstanceEditor>({
      snapshot: buildJackingDirtySnapshot
    });
  }

  private focusFirstFieldInActiveSection() {
    setTimeout(() => {
      const activeSectionCard = this.document.querySelector('.section-card') as HTMLElement | null;
      if (!activeSectionCard) {
        return;
      }

      const firstField = this.getTabbableElements(activeSectionCard)[0];
      firstField?.focus();
    });
  }

  private getTabbableElements(container: HTMLElement): HTMLElement[] {
    const selector = [
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'button:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    return Array
      .from(container.querySelectorAll<HTMLElement>(selector))
      .filter(element => this.isVisible(element));
  }

  private getRenderedLegCount(): number {
    const templateLegCount = this.editor?.template?.legLayouts?.length || 0;
    return templateLegCount || this.legsCount || 0;
  }

  private isVisible(element: HTMLElement): boolean {
    const style = window.getComputedStyle(element);
    return style.visibility !== 'hidden' && style.display !== 'none';
  }

  public openAbortModal() {
    const modal = this.modalController.create(JackingCancelModalComponent, {
      jackingFormInstanceId: this.editor?.id,
      editor: this.editor,
      currentDate: this.dailyLogDate,
      firstEventTime: this.editor.startDate,
      lastEventTime: this.editor.endDate,
      latestEventTime: this.editor.endDate || this.editor.startDate,
      activitySpansMultipleDays: this.activitySpansMultipleDays,
    }, {
      cssClass: 'jacking-cancel-modal'
    });

    modal.onDidDismiss(async (res?: { cancelled: boolean; conflict?: JackingFormConflictResult }) => {
      this.concurrentChanges.resumeLiveTracking();
      if (res?.cancelled) {
        this.dirty.markClean(this.editor);
        this.navCtrl.pop();
      }
      if (res?.conflict) {
        await this.concurrentChanges.handleConflict(res.conflict, 'cancellation');
      }
    });

    modal.present();
  }

  public openCompleteModal() {
    const modal = this.modalController.create(JackingCompleteModalComponent, {
      jackingFormInstanceId: this.editor?.id,
      editor: this.editor,
    }, {
      cssClass: 'jacking-complete-modal'
    });

    modal.onDidDismiss(async (res?: {
      completed: boolean;
      validation?: ValidationResultModel;
      conflict?: JackingFormConflictResult;
    }) => {
      this.concurrentChanges.resumeLiveTracking();
      if (res?.completed) {
        this.dirty.markClean(this.editor);
        this.navCtrl.pop();
      }

      if (res?.validation) {
        this.validationResult = res.validation;
        this.goToValidationError();
      }
      if (res?.conflict) {
        await this.concurrentChanges.handleConflict(res.conflict, 'completion');
      }
    });

    modal.present();
  }

  openFileUploadModal() {
    const modal = this.modalController.create(JackingActivityFileUploadModalComponent, {
      jackingFormInstanceId: this.editor.id
    }, {
      cssClass: 'jacking-activity-upload-file'
    });

    modal.onDidDismiss(() => this.concurrentChanges.resumeLiveTracking());
    modal.present();
  }

  goToValidationError(err?: any) {
    if (this.mainContent) {
      this.mainContent.scrollTop = 0;
    }
  }

  public isEditorDirtyForConcurrentChanges(): boolean {
    return this.dirty.isDirty(this.editor);
  }

  public markEditorCleanForConcurrentChanges(editor: JackingFormInstanceEditor): void {
    this.dirty.markClean(editor);
  }

  public async commitConcurrentEditor(
    merged: JackingFormInstanceEditor,
    server: JackingFormInstanceEditor
  ): Promise<void> {
    this.editor = merged;
    await this.refreshCompletedDayState();
    this.applyLockedPreviousDayStateToEvents();
    this.isReadOnly = String(this.editor.statusId).toLowerCase() === 'cancelled';
    this.setSupportContext();
  }

  getSectionStatus(section: JackingSectionTemplateEditor): JackingSectionStatus {
    const detail = getJackingSectionStatusDetail(this.editor, section, this.legsCount || 0);
    return detail.status;
  }

  // Finds the earliest and latest relevant times on the form for multi-day mode.
  private getFormTimeBounds(): { minDateTime: any; maxDateTime: any } | null {
    const candidates: Array<any> = [];

    if (this.editor?.startDate?.dateTime) {
      candidates.push(this.editor.startDate.dateTime);
    }

    if (this.editor?.endDate?.dateTime) {
      candidates.push(this.editor.endDate.dateTime);
    }

    if (this.editor?.cancellationTime?.dateTime) {
      candidates.push(this.editor.cancellationTime.dateTime);
    }

    for (const row of this.editor?.jackingEvents?.eventRows || []) {
      if (row?.eventDate?.dateTime) {
        candidates.push(row.eventDate.dateTime);
      }
    }

    const finalValues: any = this.editor?.jackingFinalValues;
    const finalBlocks = [
      finalValues?.finalLegValues,
      finalValues?.finalPenetrations,
      finalValues?.differenceFromExpected,
      finalValues?.finalRpd,
      finalValues?.finalAirGap
    ];

    for (const block of finalBlocks) {
      const dt = block?.timeUtc?.dateTime ?? block?.timeUtc;
      if (dt) {
        candidates.push(dt);
      }
    }

    for (const leg of this.editor?.jackingLegSummary?.legs || []) {
      const legTimes = [
        leg?.legMaxPullingLoadTime?.dateTime,
        leg?.legFreeInHolesExtTime?.dateTime,
        leg?.clearOfSeaBedExtTime?.dateTime
      ];

      for (const dt of legTimes) {
        if (dt) {
          candidates.push(dt);
        }
      }
    }

    if (!candidates.length) {
      return null;
    }

    return candidates.reduce((bounds, current) => ({
      minDateTime: this.dateService.isFirstDateLargerOffsetsPreApplied(bounds.minDateTime, current) ? current : bounds.minDateTime,
      maxDateTime: this.dateService.isFirstDateLargerOffsetsPreApplied(current, bounds.maxDateTime) ? current : bounds.maxDateTime
    }), {
      minDateTime: candidates[0],
      maxDateTime: candidates[0]
    });
  }

  private async refreshCompletedDayState(): Promise<void> {
    this.completedDayByIso = {};

    const timeBounds = this.getFormTimeBounds();
    if (!timeBounds?.minDateTime || !timeBounds?.maxDateTime) {
      return;
    }

    let dayCursor = await this.dateService.getStartOfDay(timeBounds.minDateTime);
    const lastDay = await this.dateService.getStartOfDay(timeBounds.maxDateTime);

    while (!this.dateService.isFirstDayLargerOffsetsPreApplied(dayCursor, lastDay)) {
      const isoDate = dayCursor.toISODate();
      const filters = new CompleteDayFilters();
      filters.date = dayCursor;

      this.completedDayByIso[isoDate] = await this.completeDayService.checkDay(filters);
      dayCursor = await this.dateService.addDuration(dayCursor, {day: 1}, true);
    }
  }

  private applyLockedPreviousDayStateToEvents(): void {
    const currentIsoDate = this.dailyLogDate?.dateTime?.toISODate();
    if (!currentIsoDate) {
      return;
    }

    for (const row of this.editor?.jackingEvents?.eventRows || []) {
      const rowIsoDate = row?.eventDate?.dateTime?.toISODate();
      (row as any)._isReadonly = !!rowIsoDate
        && rowIsoDate < currentIsoDate
        && !!this.completedDayByIso[rowIsoDate];
    }
  }

  private setSupportContext() {
    this.supportContextService.setComponentContext(
      SupportContextKey.JackingActivities,
      'Jacking Activity Form',
      'JackingActivityV2EditPage',
      {
        type: this.getFormType(),
        starting: this.getStartingTime(),
        currentTab: this.getCurrentTabName()
      }
    );
  }

  private getFormType(): string {
    if (!this.editor) {
      return '';
    }

    const formType = `${this.editor.formTypeId || ''}`;

    return formType
      ? formType.charAt(0).toUpperCase() + formType.slice(1)
      : '';
  }

  private getStartingTime(): string {
    if (!this.editor || !this.editor.startDate || !this.editor.startDate.dateTime) {
      return '';
    }

    const offset = this.editor.startOffset || 0;
    const localStart = this.editor.startDate.dateTime.plus({hours: offset});
    const offsetLabel = offset >= 0 ? `+${offset}` : `${offset}`;

    return `${localStart.toFormat('HH:mm')} (${offsetLabel})`;
  }

  private getCurrentTabName(): string {
    const section = this.getActiveSection();

    return section ? this.getLongSectionTitle(section) : '';
  }
}



// WEBPACK FOOTER //
// ./src/pages/jacking-activities/v2/jacking-activity-edit-v2/jacking-activity-edit-v2.ts